import { inject, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ApplianceDetailsVisibilityService } from '@modules/appliance-details/appliance-visibility.service';
import { RxIntuitiveApplianceVisibilityService } from '@modules/rx-intuitive-appliance-details/rx-intuitive-appliance-visibility.service';
import { DentureDetailsVisibilityService } from '@modules/denture-details/denture-details-visibility.service';
import { RxIntuitiveDentureVisibilityService } from '@modules/rx-intuitive-denture-procedure/rx-intuitive-denture-visibility.service';
import { SurgicalGuideVisibilityService } from '@modules/rx-intuitive-scan-surgical-guide/surgical-guide-visibility.service';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { RxTemplate } from '@shared/rx-templates/rx-template.model';

/**
 * ## Visibility token convention
 *
 * Use an InjectionToken<Observable<boolean>> when:
 * - A parent context (facade, service) needs a single boolean stream from a child section.
 * - The contract should be narrow: "is this section non-empty / visible?" — nothing else.
 * - Template-driven flags (ui.xxx.disabled / hidden) belong here, not in facades directly.
 *
 * Use a VisibilityService (XxxVisibilityService) when:
 * - A section has multiple fields with independent visibility rules.
 * - Consumers need fields$ (per-field flags) for ngIf and store clearing.
 * - procedureType is now part of each section's template (ui.xxx.items.procedureType.hidden)
 * and lives in the relevant VisibilityService, not as a standalone token.
 *
 * Do NOT inject RxTemplateService directly in facades or components.
 * Wrap template flags in tokens (single flag) or VisibilityService (multi-field).
 *
 * Token naming:
 * - RX_INTUITIVE_APPLIANCE_SECTION_VISIBLE$ → intuitive flow (procedure-selected-page)
 * - APPLIANCE_DETAILS_SECTION_VISIBLE$      → v1 flow (rx-for-doctor)
 * - DENTURE_SECTION_VISIBLE$               → intuitive flow (procedure-selected-page)
 * - DENTURE_DETAILS_SECTION_VISIBLE$       → v1 flow (rx-for-doctor)
 */

// ── Template flag helpers for single-flag tokens ─────────────────────────────
// Pure functions that evaluate a flag for a target Send To using the same
// template mapping as the corresponding InjectionToken factory.
// Used by dirty-check services to evaluate flags for a target sendTo.

/** Notes visibility flag for a target Send To (same rule as IS_NOTES_VISIBLE$ token). */
export function isNotesVisibleForSendTo(template: Readonly<RxTemplate>): boolean {
	return !template.ui.notes.disabled;
}

/** Due date visibility flag for a target Send To (same rule as IS_DUE_DATE_VISIBLE$ token). */
export function isDueDateVisibleForSendTo(template: Readonly<RxTemplate>): boolean {
	return !template.ui.order.items.dueDate.disabled;
}

/** Attachments visibility flag for a target Send To (same rule as IS_ATTACHMENTS_VISIBLE$ token). */
export function isAttachmentsVisibleForSendTo(template: Readonly<RxTemplate>): boolean {
	return !template.ui.attachments.disabled;
}

// ── Section non-empty tokens — rx-intuitive flow ─────────────────────────────
// Used by ProcedureSectionVisibilityService (intuitive) to gate entire sections.

export const RX_INTUITIVE_APPLIANCE_DETAILS_VISIBLE$ = new InjectionToken<Observable<boolean>>('RX_INTUITIVE_APPLIANCE_DETAILS_VISIBLE$', {
	providedIn: 'root',
	factory: () => inject(RxIntuitiveApplianceVisibilityService).isSectionVisible$
});

export const RX_INTUITIVE_DENTURE_DETAILS_VISIBLE$ = new InjectionToken<Observable<boolean>>('RX_INTUITIVE_DENTURE_DETAILS_VISIBLE$', {
	providedIn: 'root',
	factory: () => inject(RxIntuitiveDentureVisibilityService).isSectionVisible$
});

export const SURGICAL_GUIDE_SECTION_VISIBLE$ = new InjectionToken<Observable<boolean>>('SURGICAL_GUIDE_SECTION_VISIBLE$', {
	providedIn: 'root',
	factory: () => inject(SurgicalGuideVisibilityService).isSectionVisible$
});

// ── Section non-empty tokens — v1 flow ───────────────────────────────────────
// Used by rx-for-doctor to gate the v1 appliance-details section.
// Gates on EnableApplianceDetails SWO — same as original isExtendedApplianceDetailsVisible$.

export const APPLIANCE_DETAILS_SECTION_VISIBLE$ = new InjectionToken<Observable<boolean>>('APPLIANCE_DETAILS_SECTION_VISIBLE$', {
	providedIn: 'root',
	factory: () => inject(ApplianceDetailsVisibilityService).isSectionVisible$
});

export const DENTURE_DETAILS_SECTION_VISIBLE$ = new InjectionToken<Observable<boolean>>('DENTURE_DETAILS_SECTION_VISIBLE$', {
	providedIn: 'root',
	factory: () => inject(DentureDetailsVisibilityService).isSectionVisible$
});

// ── Single-element visibility tokens ────────────────────────────────────────
// Template-driven flags for individual UI elements with no sub-fields.

/** Notes card — hidden when template.ui.notes.disabled = true. */
export const IS_NOTES_VISIBLE$ = new InjectionToken<Observable<boolean>>('IS_NOTES_VISIBLE$', {
	providedIn: 'root',
	factory: () =>
		inject(RxTemplateService).template$.pipe(
			map(t => !t.ui.notes.disabled),
			shareReplay({ bufferSize: 1, refCount: true })
		)
});

/** Due date display — hidden when template.ui.order.items.dueDate.disabled = true (e.g. labBrokerDefault). */
export const IS_DUE_DATE_VISIBLE$ = new InjectionToken<Observable<boolean>>('IS_DUE_DATE_VISIBLE$', {
	providedIn: 'root',
	factory: () =>
		inject(RxTemplateService).template$.pipe(
			map(t => !t.ui.order.items.dueDate.disabled),
			shareReplay({ bufferSize: 1, refCount: true })
		)
});

/** Attachments section — hidden when template.ui.attachments.disabled = true. */
export const IS_ATTACHMENTS_VISIBLE$ = new InjectionToken<Observable<boolean>>('IS_ATTACHMENTS_VISIBLE$', {
	providedIn: 'root',
	factory: () =>
		inject(RxTemplateService).template$.pipe(
			map(t => !t.ui.attachments.disabled),
			shareReplay({ bufferSize: 1, refCount: true })
		)
});
